import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type Keycloak from 'keycloak-js';
import kc from '@/lib/keycloak';

interface AuthCtx {
  authenticated: boolean;
  ready: boolean;
  kc: Keycloak;
  tenant: TenantInfo | null;
  login: () => void;
  logout: () => void;
}

export interface TenantInfo {
  // Fields we surface from the JWT for display. The full tenant record comes
  // from GET /v1/me — this is just the bits we can read locally without a
  // round-trip.
  sub: string;
  email?: string;
  name?: string;
}

const AuthContext = createContext<AuthCtx | undefined>(undefined);

// Singleton init guard. React 19's StrictMode double-invokes effects in dev
// which would otherwise cause keycloak-js to throw "Keycloak instance is
// already initialized."
let kcInitPromise: Promise<boolean> | null = null;

function initKeycloak() {
  if (!kcInitPromise) {
    kcInitPromise = kc.init({
      onLoad: 'check-sso',
      pkceMethod: 'S256',
      checkLoginIframe: false,
      silentCheckSsoRedirectUri:
        window.location.origin + '/silent-check-sso.html',
      // Critical for the public landing page: when the silent iframe can't
      // run (Firefox strict-cookies, CSP, third-party-cookie blockers),
      // keycloak-js's default behaviour is a full-page redirect to the
      // Keycloak server with prompt=none — visitor sees "/ → SSO → /"
      // flicker on every reload. Disabling the fallback means a failed
      // silent check leaves us as "unauthenticated" instead; user can
      // sign in explicitly from the landing CTAs.
      silentCheckSsoFallback: false,
    });
  }
  return kcInitPromise;
}

function tenantFromToken(): TenantInfo | null {
  if (!kc.authenticated || !kc.tokenParsed) return null;
  const t = kc.tokenParsed as Record<string, unknown>;
  return {
    sub: String(t.sub ?? ''),
    email: typeof t.email === 'string' ? t.email : undefined,
    name:
      typeof t.name === 'string'
        ? t.name
        : typeof t.preferred_username === 'string'
          ? (t.preferred_username as string)
          : undefined,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const refreshTimer = useRef<number | null>(null);

  useEffect(() => {
    let mounted = true;

    initKeycloak()
      .then((auth) => {
        if (!mounted) return;
        setAuthenticated(auth);
        setTenant(tenantFromToken());
        setReady(true);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Keycloak init failed', err);
        if (mounted) setReady(true);
      });

    kc.onAuthSuccess = () => {
      setAuthenticated(true);
      setTenant(tenantFromToken());
    };
    kc.onAuthLogout = () => {
      setAuthenticated(false);
      setTenant(null);
    };
    kc.onTokenExpired = () => {
      kc.updateToken(30).catch(() => kc.login());
    };

    return () => {
      mounted = false;
    };
  }, []);

  // Periodic refresh: every 60s, refresh if the token expires within the next
  // 30s. Cheap to run; avoids racing API calls against token expiry.
  useEffect(() => {
    if (!authenticated) return;
    const id = window.setInterval(() => {
      kc.updateToken(30).catch(() => {
        // Refresh token also expired — bounce through login.
        kc.login();
      });
    }, 60_000);
    refreshTimer.current = id;
    return () => window.clearInterval(id);
  }, [authenticated]);

  const login = useCallback(() => {
    kc.login({ redirectUri: window.location.origin + '/dashboard' });
  }, []);

  const logout = useCallback(() => {
    kc.logout({ redirectUri: window.location.origin + '/' });
  }, []);

  return (
    <AuthContext.Provider
      value={{ authenticated, ready, kc, tenant, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
